import * as React from 'react';
import { formatNumber, t } from '@reos/i18n';
import { Card } from './CaseUiPrimitives.js';
import {
  addParcelOutsideLv,
  checkParcelOwner,
  fetchOffer,
  removeParcel,
  requestOfferGraphic,
  setParcelSelected,
  submitLvSheet,
  type LvSheetSource,
  type OfferFindingDto,
  type OfferGraphicDto,
  type OfferOverviewDto,
  type OfferParcelDto,
  type OfferPreset,
} from '../lib/offer-api.js';

/**
 * Krok L1 bod 2 — VYBER SDRUZENE NABIDKY V ADMINU.
 *
 * Komponenta nic nepocita: stavy parcel, soucty vymer, cenu a kontroly celku
 * vraci `GET …/offer` (server, `@reos/duediligence`). Duvod je stejny jako u
 * mapovych vrstev M1 — dve implementace tehoz pravidla se rozejdou a klient
 * by pak tvrdil neco jineho nez report.
 *
 * Texty jdou VYHRADNE pres i18n: domenove popisky (stav parcely, nalez,
 * zpusob overeni) z katalogu `dd.offer.*` (tyz slovnik jako report a API),
 * chrome panelu z `webAdmin.offer.*`.
 */

const LOCALE = 'cs';
const PRESETS: readonly OfferPreset[] = ['LISTING', 'LEGAL', 'HISTORY'];
const LV_SOURCES: readonly LvSheetSource[] = ['NAHLIZENI_TEXT', 'PAID_EXTRACT'];

type StateKey = `dd.offer.state.${OfferParcelDto['state']}`;
type FindingKey = `dd.offer.finding.${OfferFindingDto['code']}`;

/**
 * Klice polozek ceniku maji TECKU (`offer.base`), a tecka je v i18n oddelovac
 * urovni - proto vlastni mapovani na bezteckovy klic, ne mechanicky prevod
 * (stejny duvod jako `SOURCE_LABEL_KEYS` v `maps/layers.ts`).
 */
const CHARGE_ITEM_KEYS: Readonly<Record<string, 'dd.offer.charge.item.offerBase'>> = {
  'offer.base': 'dd.offer.charge.item.offerBase',
  'lv.paid_extract': 'dd.offer.charge.item.lvPaidExtract' as 'dd.offer.charge.item.offerBase',
  'offer.added_parcel_audit':
    'dd.offer.charge.item.offerAddedParcelAudit' as 'dd.offer.charge.item.offerBase',
};

function chargeItemLabel(itemKey: string): string {
  const key = CHARGE_ITEM_KEYS[itemKey];
  return key === undefined ? itemKey : t(key, { locale: LOCALE });
}

/** Vymera v m2 se skupinami tisicu - „2 964 m²", ne „2964 m²". */
function area(value: number): string {
  return formatNumber(Math.round(value), { locale: LOCALE });
}

/**
 * Castka v mene odpovedi. Stejny vzor jako `naklady-inzerce.tsx` — meny
 * formatuje `Intl`, ne i18n retezec, aby v nem nebyl zapecený symbol.
 */
function formatAmount(amount: string, currency: string): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount} ${currency}`;
  return new Intl.NumberFormat('cs-CZ', { style: 'currency', currency }).format(value);
}

function stateLabel(parcel: OfferParcelDto): string {
  return t(`dd.offer.state.${parcel.state}` as StateKey, { locale: LOCALE });
}

function verificationLabel(parcel: OfferParcelDto): string {
  const method = parcel.verificationMethod;
  if (!parcel.ownershipVerified || method === null) {
    return t('dd.offer.verification.none', { locale: LOCALE });
  }
  const key =
    method === 'PAID_EXTRACT'
      ? 'dd.offer.verification.PAID_EXTRACT'
      : method === 'NAHLIZENI_CONFIRMED'
        ? 'dd.offer.verification.NAHLIZENI_CONFIRMED'
        : 'dd.offer.verification.LV_SHEET';
  const label = t(key, { locale: LOCALE });
  return parcel.verifiedLvNumber === null ? label : `${label} (LV ${parcel.verifiedLvNumber})`;
}

/** Nalez -> veta. Cisla (vymera diry, pocet casti) jde do ICU zpravy. */
function findingText(finding: OfferFindingDto): string {
  const values: Record<string, string | number> = { ...finding.values };
  // Vymera diry je cislo - do vety patri se skupinami tisicu.
  if (typeof values['areaM2'] === 'number') values['areaM2'] = area(values['areaM2']);
  return t(`dd.offer.finding.${finding.code}` as FindingKey, {
    locale: LOCALE,
    values: { ...values, parcels: finding.parcels.join(', ') },
  });
}

function severityLabel(severity: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW'): string {
  return t(`dd.offer.severity.${severity}` as `dd.offer.severity.CRITICAL`, { locale: LOCALE });
}

function accessText(access: OfferOverviewDto['wholeness']['access']): string {
  const verdict = t(
    `webAdmin.offer.checks.access${access.verdict}` as 'webAdmin.offer.checks.accessYES',
    { locale: LOCALE, values: { parcels: access.roadParcels.join(', ') } },
  );
  return t('webAdmin.offer.checks.access', { locale: LOCALE, values: { verdict } });
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : t('webAdmin.offer.error', { locale: LOCALE });
}

export interface OfferPanelProps {
  readonly caseId: string;
  /**
   * Krok L1: parcela, na kterou makler kliknul v mape. Panel z ni jen
   * PREDVYPLNI pole - pridani je porad vedomy klik, protoze muze stat penize.
   */
  readonly picked?: { readonly label: string; readonly kuCode: number | null } | null | undefined;
  /** Zmena vyberu - nadrazena sekce si prekresli geometrii v mape. */
  readonly onChanged?: (() => void) | undefined;
}

export function OfferPanel({
  caseId,
  picked = null,
  onChanged,
}: OfferPanelProps): React.JSX.Element {
  const [offer, setOffer] = React.useState<OfferOverviewDto | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [lvText, setLvText] = React.useState('');
  const [lvSource, setLvSource] = React.useState<LvSheetSource>('NAHLIZENI_TEXT');
  const [lvPaid, setLvPaid] = React.useState(false);
  const [verifyTarget, setVerifyTarget] = React.useState<OfferParcelDto | null>(null);
  const [verifyText, setVerifyText] = React.useState('');
  const [verifyPaid, setVerifyPaid] = React.useState(false);
  const [verifyVerdict, setVerifyVerdict] = React.useState<string | null>(null);
  const [newParcelLabel, setNewParcelLabel] = React.useState('');
  const [graphics, setGraphics] = React.useState<readonly OfferGraphicDto[]>([]);
  const [archiveYear, setArchiveYear] = React.useState('2012');

  const reload = React.useCallback(async (): Promise<void> => {
    try {
      setOffer(await fetchOffer(caseId));
      setError(null);
    } catch (e: unknown) {
      setError(errorMessage(e));
    }
  }, [caseId]);

  React.useEffect(() => {
    void reload();
  }, [reload]);

  // Klik do mapy predvyplni pole „pridat parcelu" - nic nepridava sam.
  React.useEffect(() => {
    if (picked !== null && picked !== undefined) setNewParcelLabel(picked.label);
  }, [picked]);

  /** Kazda akce konci PREKRESLENIM ze serveru - klient si stav nedopocitava. */
  const run = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await reload();
      onChanged?.();
    } catch (e: unknown) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const hasSheet = (offer?.lvSheets.length ?? 0) > 0;

  return (
    <div className="flex flex-col gap-4" data-testid="case-offer">
      <Card
        heading={t('webAdmin.offer.heading', { locale: LOCALE })}
        hint={t('webAdmin.offer.hint', { locale: LOCALE })}
        testId="case-offer-lv"
      >
        {error !== null && (
          <p
            role="alert"
            style={{ color: 'var(--rh-danger)', margin: '0 0 10px' }}
            data-testid="case-offer-error"
          >
            {error}
          </p>
        )}

        <p style={{ margin: '0 0 8px', color: 'var(--rh-text-muted)', fontSize: 'var(--rh-size-meta)' }}>
          {t('webAdmin.offer.lv.hint', { locale: LOCALE })}
        </p>
        {offer?.freeVerificationUrl != null && (
          <p style={{ margin: '0 0 8px' }}>
            <a href={offer.freeVerificationUrl} target="_blank" rel="noreferrer noopener">
              {t('webAdmin.offer.lv.openNahlizeni', { locale: LOCALE })}
            </a>
          </p>
        )}
        <label style={{ display: 'block', fontSize: 'var(--rh-size-footnote)', marginBottom: 4 }} htmlFor="offer-lv-text">
          {t('webAdmin.offer.lv.textLabel', { locale: LOCALE })}
        </label>
        <textarea
          id="offer-lv-text"
          rows={6}
          value={lvText}
          placeholder={t('webAdmin.offer.lv.textPlaceholder', { locale: LOCALE })}
          onChange={(event) => {
            setLvText(event.target.value);
          }}
          style={{ width: '100%', fontFamily: 'ui-monospace, monospace', fontSize: 'var(--rh-size-meta)' }}
          data-testid="case-offer-lv-text"
        />
        <div className="flex items-center gap-3 mt-2 flex-wrap">
          <label style={{ fontSize: 'var(--rh-size-footnote)' }}>
            {t('webAdmin.offer.lv.sourceLabel', { locale: LOCALE })}{' '}
            <select
              value={lvSource}
              onChange={(event) => {
                const next = event.target.value as LvSheetSource;
                setLvSource(next);
                if (next !== 'PAID_EXTRACT') setLvPaid(false);
              }}
              data-testid="case-offer-lv-source"
            >
              {LV_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {t(
                    source === 'PAID_EXTRACT'
                      ? 'dd.offer.verification.PAID_EXTRACT'
                      : 'dd.offer.verification.NAHLIZENI_CONFIRMED',
                    { locale: LOCALE },
                  )}
                </option>
              ))}
            </select>
          </label>
          <label style={{ fontSize: 'var(--rh-size-footnote)' }}>
            <input
              type="checkbox"
              checked={lvPaid}
              disabled={lvSource !== 'PAID_EXTRACT'}
              onChange={(event) => {
                setLvPaid(event.target.checked);
              }}
              data-testid="case-offer-lv-paid"
            />{' '}
            {t('webAdmin.offer.lv.paidLabel', { locale: LOCALE })}
          </label>
          <button
            type="button"
            className="rh-btn"
            disabled={busy || lvText.trim() === ''}
            onClick={() => {
              void run(async () => {
                await submitLvSheet(caseId, { source: lvSource, text: lvText, paid: lvPaid });
                setLvText('');
              });
            }}
            data-testid="case-offer-lv-submit"
          >
            {busy
              ? t('webAdmin.offer.lv.submitting', { locale: LOCALE })
              : t('webAdmin.offer.lv.submit', { locale: LOCALE })}
          </button>
        </div>

        {offer !== null && offer.lvSheets.length > 0 && (
          <ul
            style={{ margin: '12px 0 0', paddingLeft: 18, fontSize: 'var(--rh-size-footnote)' }}
            data-testid="case-offer-sheets"
          >
            {offer.lvSheets.map((sheet) => (
              <li key={sheet.id}>
                {t('webAdmin.offer.lv.sheetLine', {
                  locale: LOCALE,
                  values: {
                    lvNumber: sheet.lvNumber,
                    kuName: sheet.kuName ?? String(sheet.kuCode),
                    ownerCount: sheet.ownerCount,
                    shares: sheet.ownerShares.join(', '),
                  },
                })}
                {!sheet.sameOwnerAsOffer &&
                  ` — ${t('webAdmin.offer.lv.otherOwner', { locale: LOCALE })}`}
                {sheet.warnings.length > 0 && (
                  <ul style={{ margin: 0, paddingLeft: 16, color: 'var(--rh-warning)' }}>
                    {sheet.warnings.map((warning) => (
                      <li key={`${sheet.id}-${warning.code}-${warning.detail ?? ''}`}>
                        {t(
                          `dd.offer.lvWarning.${warning.code}` as 'dd.offer.lvWarning.lv_number_missing',
                          { locale: LOCALE, values: { detail: warning.detail ?? '' } },
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Card
        heading={t('webAdmin.offer.parcels.heading', { locale: LOCALE })}
        hint={t('webAdmin.offer.parcels.hint', { locale: LOCALE })}
        testId="case-offer-parcels"
      >
        {offer === null || offer.parcels.length === 0 ? (
          <p style={{ margin: 0, color: 'var(--rh-text-muted)' }}>
            {t('webAdmin.offer.parcels.empty', { locale: LOCALE })}
          </p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 'var(--rh-size-footnote)' }}>
            <thead>
              <tr style={{ textAlign: 'left', color: 'var(--rh-text-muted)' }}>
                <th>{t('webAdmin.offer.parcels.inOffer', { locale: LOCALE })}</th>
                <th>{t('webAdmin.offer.parcels.stateHeader', { locale: LOCALE })}</th>
                <th>{t('webAdmin.offer.parcels.areaHeader', { locale: LOCALE })}</th>
                <th>{t('webAdmin.offer.parcels.landTypeHeader', { locale: LOCALE })}</th>
                <th>{t('webAdmin.offer.parcels.verificationHeader', { locale: LOCALE })}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {offer.parcels.map((parcel) => (
                <tr
                  key={parcel.id}
                  style={{ borderTop: '1px solid var(--rh-rule)' }}
                  data-testid={`case-offer-parcel-${parcel.label}`}
                >
                  <td>
                    <label>
                      <input
                        type="checkbox"
                        checked={parcel.selected}
                        disabled={
                          busy || (parcel.origin === 'OUTSIDE_LV' && !parcel.ownershipVerified)
                        }
                        onChange={(event) => {
                          void run(async () => {
                            await setParcelSelected(caseId, parcel.id, event.target.checked);
                          });
                        }}
                        data-testid={`case-offer-select-${parcel.label}`}
                      />{' '}
                      <strong>{parcel.label}</strong>
                    </label>
                  </td>
                  <td>{stateLabel(parcel)}</td>
                  <td>{parcel.areaKnM2 === null ? '—' : `${area(parcel.areaKnM2)} m²`}</td>
                  <td>{parcel.landType ?? '—'}</td>
                  <td>{verificationLabel(parcel)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {parcel.origin === 'OUTSIDE_LV' && !parcel.ownershipVerified && (
                      <button
                        type="button"
                        className="rh-btn"
                        disabled={busy}
                        onClick={() => {
                          setVerifyTarget(parcel);
                          setVerifyText('');
                          setVerifyVerdict(null);
                        }}
                        data-testid={`case-offer-verify-${parcel.label}`}
                      >
                        {t('webAdmin.offer.parcels.verify', { locale: LOCALE })}
                      </button>
                    )}{' '}
                    <button
                      type="button"
                      className="rh-btn"
                      disabled={busy}
                      onClick={() => {
                        void run(async () => {
                          await removeParcel(caseId, parcel.id);
                        });
                      }}
                      data-testid={`case-offer-remove-${parcel.label}`}
                    >
                      {t('webAdmin.offer.parcels.remove', { locale: LOCALE })}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <div className="flex items-end gap-2 mt-3 flex-wrap">
          <label style={{ fontSize: 'var(--rh-size-footnote)' }}>
            {t('webAdmin.offer.parcels.addLabel', { locale: LOCALE })}{' '}
            <input
              value={newParcelLabel}
              onChange={(event) => {
                setNewParcelLabel(event.target.value);
              }}
              data-testid="case-offer-add-label"
            />
          </label>
          <button
            type="button"
            className="rh-btn"
            disabled={busy || !hasSheet || newParcelLabel.trim() === ''}
            title={hasSheet ? undefined : t('dd.offer.rejected.no_lv_sheet', { locale: LOCALE })}
            onClick={() => {
              void run(async () => {
                const kuCode = offer?.lvSheets[0]?.kuCode;
                if (kuCode === undefined) return;
                await addParcelOutsideLv(caseId, { kuCode, label: newParcelLabel.trim() });
                setNewParcelLabel('');
              });
            }}
            data-testid="case-offer-add"
          >
            {t('webAdmin.offer.parcels.add', { locale: LOCALE })}
          </button>
        </div>

        {verifyTarget !== null && (
          <div
            style={{ marginTop: 12, borderTop: '1px solid var(--rh-border)', paddingTop: 12 }}
            data-testid="case-offer-verify-form"
          >
            <h3 style={{ fontSize: 'var(--rh-size-body)', margin: '0 0 4px' }}>
              {t('webAdmin.offer.verify.heading', {
                locale: LOCALE,
                values: { label: verifyTarget.label },
              })}
            </h3>
            <p style={{ margin: '0 0 8px', color: 'var(--rh-text-muted)', fontSize: 'var(--rh-size-meta)' }}>
              {t('webAdmin.offer.verify.hint', { locale: LOCALE })}
            </p>
            <textarea
              rows={5}
              value={verifyText}
              onChange={(event) => {
                setVerifyText(event.target.value);
              }}
              style={{ width: '100%', fontFamily: 'ui-monospace, monospace', fontSize: 'var(--rh-size-meta)' }}
              data-testid="case-offer-verify-text"
            />
            <div className="flex items-center gap-3 mt-2 flex-wrap">
              <label style={{ fontSize: 'var(--rh-size-footnote)' }}>
                <input
                  type="checkbox"
                  checked={verifyPaid}
                  onChange={(event) => {
                    setVerifyPaid(event.target.checked);
                  }}
                  data-testid="case-offer-verify-paid"
                />{' '}
                {t('webAdmin.offer.lv.paidLabel', { locale: LOCALE })}
              </label>
              <button
                type="button"
                className="rh-btn"
                disabled={busy || verifyText.trim() === ''}
                onClick={() => {
                  const target = verifyTarget;
                  void run(async () => {
                    const result = await checkParcelOwner(caseId, target.id, {
                      source: verifyPaid ? 'PAID_EXTRACT' : 'NAHLIZENI_TEXT',
                      text: verifyText,
                      paid: verifyPaid,
                    });
                    setVerifyVerdict(
                      t(`dd.offer.verdict.${result.verdict}` as 'dd.offer.verdict.SAME_OWNER', {
                        locale: LOCALE,
                        values: { lvNumber: result.lvNumber },
                      }),
                    );
                    if (result.verdict !== 'DIFFERENT_OWNER') setVerifyTarget(null);
                  });
                }}
                data-testid="case-offer-verify-submit"
              >
                {t('webAdmin.offer.verify.submit', { locale: LOCALE })}
              </button>
              <button
                type="button"
                className="rh-btn"
                onClick={() => {
                  setVerifyTarget(null);
                }}
              >
                {t('webAdmin.offer.verify.cancel', { locale: LOCALE })}
              </button>
            </div>
          </div>
        )}
        {verifyVerdict !== null && (
          <p style={{ marginTop: 8, fontSize: 'var(--rh-size-footnote)' }} data-testid="case-offer-verdict">
            {verifyVerdict}
          </p>
        )}
      </Card>

      {offer !== null && (
        <div className="rh-wizard-grid">
          <Card
            heading={t('webAdmin.offer.areas.heading', { locale: LOCALE })}
            testId="case-offer-areas"
          >
            <p style={{ margin: '0 0 6px', fontWeight: 600 }}>
              {t('webAdmin.offer.areas.total', {
                locale: LOCALE,
                values: { area: area(offer.areas.areaKnM2), count: offer.areas.parcelCount },
              })}
            </p>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--rh-size-footnote)' }}>
              {offer.areas.byLandType.map((group) => (
                <li key={group.landTypeCode ?? 'null'}>
                  {t('webAdmin.offer.areas.group', {
                    locale: LOCALE,
                    values: {
                      landType: group.landType ?? '—',
                      area: area(group.areaKnM2),
                      count: group.parcelCount,
                    },
                  })}
                </li>
              ))}
            </ul>
            {offer.areas.areaMismatch && (
              <p
                style={{ marginTop: 8, color: 'var(--rh-warning)', fontSize: 'var(--rh-size-meta)' }}
                data-testid="case-offer-area-mismatch"
              >
                {t('webAdmin.offer.areas.mismatch', {
                  locale: LOCALE,
                  values: { parcels: offer.areas.mismatchedParcels.join(', ') },
                })}
              </p>
            )}
          </Card>

          <Card
            heading={t('webAdmin.offer.price.heading', { locale: LOCALE })}
            {...(offer.price.proposed
              ? { hint: t('dd.offer.charge.proposed', { locale: LOCALE }) }
              : {})}
            testId="case-offer-price"
          >
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--rh-size-footnote)' }}>
              {offer.price.lines.map((line) => (
                <li key={line.itemKey}>
                  {t('webAdmin.offer.price.line', {
                    locale: LOCALE,
                    values: {
                      item: chargeItemLabel(line.itemKey),
                      quantity: line.quantity,
                      unitAmount: formatAmount(line.unitAmount, offer.price.currency),
                    },
                  })}
                </li>
              ))}
            </ul>
            <p style={{ marginTop: 8, fontWeight: 600 }} data-testid="case-offer-price-total">
              {t('webAdmin.offer.price.total', {
                locale: LOCALE,
                values: { total: formatAmount(offer.price.total, offer.price.currency) },
              })}
            </p>
          </Card>
        </div>
      )}

      {offer !== null && (
        <Card
          heading={t('webAdmin.offer.checks.heading', { locale: LOCALE })}
          testId="case-offer-checks"
        >
          {offer.wholeness.contiguous && (
            <p style={{ margin: '0 0 6px', fontSize: 'var(--rh-size-footnote)' }}>
              {t('webAdmin.offer.checks.contiguous', {
                locale: LOCALE,
                values: { area: area(offer.wholeness.areaM2) },
              })}
            </p>
          )}
          <p style={{ margin: '0 0 6px', fontSize: 'var(--rh-size-footnote)' }} data-testid="case-offer-access">
            {accessText(offer.wholeness.access)}
          </p>
          {offer.wholeness.findings.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--rh-text-muted)' }}>
              {t('webAdmin.offer.checks.noFindings', { locale: LOCALE })}
            </p>
          ) : (
            <ul
              style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--rh-size-footnote)' }}
              data-testid="case-offer-findings"
            >
              {offer.wholeness.findings.map((finding, index) => (
                <li key={`${finding.code}-${String(index)}`}>
                  <strong>{severityLabel(finding.severity)}:</strong> {findingText(finding)}
                </li>
              ))}
            </ul>
          )}
          {offer.wholeness.severity === 'CRITICAL' && (
            <p
              style={{ marginTop: 8, color: 'var(--rh-danger)', fontSize: 'var(--rh-size-footnote)' }}
              data-testid="case-offer-blocking"
            >
              {t('webAdmin.offer.report.blocking', { locale: LOCALE })}
            </p>
          )}
        </Card>
      )}

      {offer !== null && (
        <Card
          heading={t('webAdmin.offer.suggestions.heading', { locale: LOCALE })}
          testId="case-offer-suggestions"
        >
          {offer.suggestions.length === 0 ? (
            <p style={{ margin: 0, color: 'var(--rh-text-muted)' }}>
              {t('webAdmin.offer.suggestions.empty', { locale: LOCALE })}
            </p>
          ) : (
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 'var(--rh-size-footnote)' }}>
              {offer.suggestions.map((suggestion) => (
                <li key={suggestion.ruianId}>
                  <strong>{suggestion.label}</strong>{' '}
                  {suggestion.reasons
                    .map((reason) =>
                      t(`dd.offer.suggestion.${reason}` as 'dd.offer.suggestion.ACCESS', {
                        locale: LOCALE,
                      }),
                    )
                    .join(', ')}{' '}
                  <button
                    type="button"
                    className="rh-btn"
                    disabled={busy}
                    onClick={() => {
                      void run(async () => {
                        await addParcelOutsideLv(caseId, { ruianId: suggestion.ruianId });
                      });
                    }}
                    data-testid={`case-offer-suggest-add-${suggestion.label}`}
                  >
                    {t('webAdmin.offer.suggestions.add', {
                      locale: LOCALE,
                      values: { label: suggestion.label },
                    })}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>
      )}

      <Card
        heading={t('webAdmin.offer.graphic.heading', { locale: LOCALE })}
        hint={t('webAdmin.offer.graphic.hint', { locale: LOCALE })}
        testId="case-offer-graphic"
      >
        <div className="flex items-center gap-2 flex-wrap">
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              className="rh-btn"
              disabled={busy || (offer?.areas.parcelCount ?? 0) === 0}
              onClick={() => {
                void run(async () => {
                  const created = await requestOfferGraphic(caseId, {
                    preset,
                    ...(preset === 'HISTORY' ? { archiveYear } : {}),
                  });
                  setGraphics((current) => [
                    ...current.filter((item) => item.mapId !== created.mapId),
                    created,
                  ]);
                });
              }}
              data-testid={`case-offer-graphic-${preset}`}
            >
              {t(`dd.offer.preset.${preset}` as 'dd.offer.preset.LISTING', { locale: LOCALE })}
            </button>
          ))}
          <label style={{ fontSize: 'var(--rh-size-footnote)' }}>
            {t('webAdmin.offer.graphic.archiveYear', { locale: LOCALE })}{' '}
            <input
              value={archiveYear}
              inputMode="numeric"
              size={5}
              onChange={(event) => {
                setArchiveYear(event.target.value);
              }}
              data-testid="case-offer-graphic-year"
            />
          </label>
        </div>
        {graphics.length > 0 && (
          <ul
            style={{ margin: '10px 0 0', paddingLeft: 18, fontSize: 'var(--rh-size-footnote)' }}
            data-testid="case-offer-graphic-list"
          >
            {graphics.map((graphic) => (
              <li key={graphic.mapId}>
                {t(
                  graphic.status === 'DONE'
                    ? 'webAdmin.offer.graphic.ready'
                    : 'webAdmin.offer.graphic.queued',
                  {
                    locale: LOCALE,
                    values: {
                      preset: t(
                        `dd.offer.preset.${graphic.preset ?? 'LISTING'}` as 'dd.offer.preset.LISTING',
                        { locale: LOCALE },
                      ),
                      format: t(
                        `dd.offer.format.${graphic.format ?? 'RATIO_3_2'}` as 'dd.offer.format.RATIO_3_2',
                        { locale: LOCALE },
                      ),
                    },
                  },
                )}
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
